import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, MenuItem, Popover } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { find, isEmpty, map, startCase, toLower } from 'lodash';
import { LoadingButton } from '@mui/lab';

import DateField from 'common/forms/DateField';
import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import orderStatus from 'common/constants/orderStatus';
import { ExternalOrdersFieldKeys } from 'enums/order.enum';
import { validateMinLength } from 'common/forms/formValidations';
import { updateProcessedExternalOrdersFilter } from 'store/thunks/workQueueThunks';
import { casingFormat } from 'common/utils';
import AutoCompleteMultipleSelect from 'common/forms/AutoCompleteMultipleSelect';

const dateToUTCString = (date) => {
  if (!date) return null;
  return date.toISOString();
};

const utcStringToLocalDate = (isoString) => {
  if (!isoString) return null;
  return new Date(isoString);
};

const ProcessedExternalOrdersFilterButton = () => {
  const dispatch = useDispatch();
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);

  const partner = useMemo(() => {
    return map(partners, ({ partnerName }) => casingFormat(partnerName));
  }, [partners]);

  const loading = useSelector((state) => state.workQueue.processedExternalOrders.loading);
  const filter = useSelector(({ workQueue: { processedExternalOrders } }) => ({
    ...processedExternalOrders?.filter,
    status: processedExternalOrders?.filter?.status || 'ALL',
    createdAt:
      processedExternalOrders?.filter?.createdAt &&
      utcStringToLocalDate(processedExternalOrders.filter.createdAt),
  }));

  useEffect(() => {
    if (isEmpty(filter.partnerIds)) {
      setSelectedPartners([]);
      setSelectedPartnerIds([]);
    }
  }, [filter.partnerIds]);

  const handleOpen = (event) => {
    if (filter?.partnerIds) {
      setSelectedPartnerIds(filter.partnerIds);
      setSelectedPartners(
        filter.partnerIds.map((partnerId) =>
          casingFormat(find(partners, { partnerUuid: partnerId })?.partnerName)
        )
      );
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'ProcessedExternalOrdersFilter-Popover' : undefined;

  const handlePartnerSelect = (newValue) => {
    const partnerIds = newValue.map(
      (partnerName) =>
        partners.find((partnerData) => casingFormat(partnerData.partnerName) === partnerName)
          ?.partnerUuid
    );

    setSelectedPartners(newValue);
    setSelectedPartnerIds(partnerIds);
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateProcessedExternalOrdersFilter({
        filter: {
          ...formData,
          createdAt: formData.createdAt && dateToUTCString(formData.createdAt),
          status: formData.status === 'ALL' ? undefined : formData.status,
          partnerIds: !isEmpty(selectedPartnerIds) ? selectedPartnerIds : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={filter}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <Grid item sx={{ py: 0.5 }}>
                      <TextField
                        label='Order Id'
                        name={ExternalOrdersFieldKeys.ORDER_ID}
                        id='WorkQueue-ProcessedExternalOrdersFilter-orderId-input'
                        validations={[validateMinLength('Minimum of 3 characters', 3)]}
                      />
                    </Grid>

                    <Grid item sx={{ py: 0.5 }}>
                      <TextField
                        label='Patient Name'
                        name={ExternalOrdersFieldKeys.PATIENT_NAME}
                        id='WorkQueue-ProcessedExternalOrdersFilter-patientName-input'
                        validations={[validateMinLength('Minimum of 3 characters', 3)]}
                      />
                    </Grid>
                    <AutoCompleteMultipleSelect
                      label='Partner'
                      name='partnerIds'
                      id='WorkQueue-ProcessedExternalOrderFilterButton-partnerIds-input'
                      multiple
                      defaultValue={selectedPartners}
                      options={partner}
                      sortOptions
                      value={selectedPartners}
                      onChange={(event, newValue) => handlePartnerSelect(newValue)}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <SelectField
                      label='Status'
                      name={ExternalOrdersFieldKeys.STATUS}
                      id='WorkQueue-ProcessedExternalOrdersFilter-status-dropdown'
                    >
                      <MenuItem value='ALL'>All</MenuItem>
                      {map(
                        Object.keys(orderStatus).filter((status) => status !== 'EXPIRED'),
                        (status, index) => (
                          <MenuItem key={index} value={status}>
                            {startCase(toLower(status))}
                          </MenuItem>
                        )
                      )}
                    </SelectField>
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Created At'
                      name={ExternalOrdersFieldKeys.CREATED_AT}
                      id='WorkQueue-ProcessedExternalOrdersFilter-createAt-dateField'
                      maxDate={new Date()}
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default ProcessedExternalOrdersFilterButton;
